import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountBalance, SideDrawer, Skeleton, withStyles } from 'vopay';
import { Button } from 'UI';
import useFetchPartnerAccountDetails from '@/components/Entities/hooks/useApi/useFetchPartnerAccountDetails';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import { useToast } from '@/providers/Toast.provider';
import AccountCard from '@/components/Entities/Accounts/AccountHeaderCards/AccountCard';
import AvailableBalanceCard from '@/components/Entities/Accounts/AccountHeaderCards/AvailableBalanceCard';
import TransactionLimitsCard from '@/components/Entities/Accounts/AccountHeaderCards/TransactionLimitsCard';
import { getAccountCredentials } from '@/components/Entities/Accounts/AccountHeaderCards/accountCredentials';
import { getOutboundLimits } from '@/components/Entities/Accounts/AccountHeaderCards/accountLimits';
import toAccountBalance from '@/components/Entities/Accounts/toAccountBalance';
import styles from './AccountDetailsDrawer.module.scss';

const cn = withStyles(styles);

/** The account to show, as a list row or an ID alone; null keeps the drawer closed. */
type AccountDetailsDrawerProps = {
  onClose: () => void;
  onViewFullDetails: (account: AccountBalance) => void;
} & (
  | {
      /** A list row, which renders at once. */
      account: AccountBalance | null;
      accountID?: never;
      /**
       * Set while a host that has only the account's id is fetching its row (the Organization
       * Overview's cards): the drawer opens on a skeleton rather than after a pause.
       */
      isFetching?: boolean;
    }
  | {
      /** An ID alone (the Onboarding Tracker has no balances): renders from the fetched details, with their first balance. */
      accountID: string | null;
      account?: never;
      isFetching?: never;
    }
);

/**
 * The quick view of an account opened from its name in a list (spec 5.5): identity and credentials,
 * balances, and transaction limits, with the route to the full details page. A list row already
 * carries the name, status and balances; the credentials, reserve policy and limit usage are fetched
 * on open.
 */
export default function AccountDetailsDrawer({
  account = null,
  accountID: accountIDProp = null,
  isFetching = false,
  onClose,
  onViewFullDetails,
}: AccountDetailsDrawerProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountDetailsDrawer' });
  const toast = useToast();
  const { canSeeAccountCredential } = useModuleFeatureFlag('accounts');
  const { account: details, fetchPartnerAccountDetails, reset } = useFetchPartnerAccountDetails();
  const [hasDetailsFailed, setHasDetailsFailed] = useState(false);

  const accountID = account?.AccountID ?? accountIDProp;

  // The row the cards render: the list's own, or one built from the details when only an ID was given.
  const row = account ?? (details ? toAccountBalance(details, null) : null);

  useEffect(() => {
    // The previous account's details must not show under a newly opened account's name.
    reset();
    setHasDetailsFailed(false);

    if (!accountID) return undefined;

    // The hook drops a superseded response, but its rejection still reaches this caller — so a
    // request the user has already moved on from must not mark the open account as failed.
    let isCurrentAccount = true;

    fetchPartnerAccountDetails(accountID).catch((error) => {
      if (!isCurrentAccount) return;

      // The hook has logged the cause. With a list row the cards still render what it carries, and the
      // policy and usage figures show as unavailable rather than loading forever, which says what is
      // missing — so no toast. With an ID alone there is nothing to show, so the failure is toasted and
      // the drawer closes, as Partner Details does when its lookup fails.
      setHasDetailsFailed(true);

      if (!account) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        toast.neutral(
          t('loadError', { defaultValue: 'Could not load the account details: {{errorMessage}}', errorMessage }),
        );

        onClose();
      }
    });

    return () => {
      isCurrentAccount = false;
    };
  }, [accountID]);

  // Undefined while loading, null when the request failed — the cards render each differently.
  const detailsOrFailure = details ?? (hasDetailsFailed ? null : undefined);

  // Same gate as the account details page: an Active account, and a partner allowed to see credentials.
  const credentials = row && row.IsActive && canSeeAccountCredential ? getAccountCredentials(row.Token) : null;

  return (
    <SideDrawer
      isOpen={Boolean(accountID) || isFetching}
      onClose={onClose}
      title={t('title', { defaultValue: 'Account Details' })}
      containerClassName={cn('drawer')}
      footer={
        <Button
          colorScheme="primary"
          size="large"
          className={cn('viewFullDetails')}
          disabled={!row}
          onClick={() => row && onViewFullDetails(row)}
        >
          {t('viewFullDetails', { defaultValue: 'View Full Details' })}
        </Button>
      }
    >
      {renderContent()}
    </SideDrawer>
  );

  function renderContent() {
    if (row) {
      return (
        <div className={cn('cards')}>
          <AccountCard account={row} credentials={credentials} />

          <AvailableBalanceCard
            currency={row.Currency}
            availableBalance={row.AvailableBalance}
            pendingBalance={row.PendingBalance}
            reserve={row.Reserve}
            rollingReserves={detailsOrFailure && (detailsOrFailure.RollingReserves ?? [])}
          />

          <TransactionLimitsCard
            currency={row.Currency}
            limits={detailsOrFailure && getOutboundLimits(detailsOrFailure)}
            usage={detailsOrFailure && (detailsOrFailure.LimitUsage?.Outbound ?? null)}
          />
        </div>
      );
    }

    // An ID alone still loading, or a host still fetching the row: placeholders where the three cards
    // go. A failed load has toasted and closed the drawer, so it shows nothing while it slides out.
    if (isFetching || (accountID && !hasDetailsFailed)) {
      return (
        <div className={cn('cards')} data-testid="accountDetailsDrawerSkeleton">
          <Skeleton height="200px" borderRadius={16} />
          <Skeleton height="160px" borderRadius={16} />
          <Skeleton height="160px" borderRadius={16} />
        </div>
      );
    }

    return null;
  }
}
